/**
 * Places model: labels and colours for place kinds, asset and rule enums; shape conversions
 * between the API and the map; summaries of rule configurations. Pure (tested in model.test.ts).
 */
import type { LucideIcon } from 'lucide-react';
import { Anchor, Boxes, Building2, CircleParking, Clock, Factory, Flag, Fuel, Gauge, House, MapPin, Warehouse, WifiOff } from 'lucide-react';
import { type FieldLookup, fieldLabel } from '@/lib/fields';
import { formatMetres } from '@/lib/format';
import { msg, t, tp } from '@/lib/i18n';
import type { SelectOption, Tone } from '@/ui';
import type { GeoShape, GeoTone, LngLat } from '@/ui/Map/GeoMap';
import type { LiveFrameUnit } from '../live/model';
import type { AssetRuleOut, AssetsLiveOut, PlaceOut, Shape } from './api';

// ---------------------------------------------------------------- place kinds

export type PlaceKind = PlaceOut['kind'];

export const PLACE_KINDS: Record<string, { label: string; tone: GeoTone; icon: LucideIcon; suggested: string[] }> = {
  depot: { label: msg('Depot'), tone: 'primary', icon: Warehouse, suggested: ['opening_hours', 'capacity', 'parking_spots'] },
  customer: { label: msg('Customer'), tone: 'good', icon: Building2, suggested: ['delivery_instructions', 'opening_hours'] },
  supplier: { label: msg('Supplier'), tone: 'accent', icon: Factory, suggested: ['opening_hours', 'loading_docks'] },
  fuel_station: { label: msg('Fuel station'), tone: 'caution', icon: Fuel, suggested: ['brand', 'fuel_card'] },
  home: { label: msg('Home'), tone: 'info', icon: House, suggested: [] },
  parking: { label: msg('Parking'), tone: 'medium', icon: CircleParking, suggested: ['parking_type', 'capacity', 'security_level'] },
  warehouse: { label: msg('Warehouse'), tone: 'accent', icon: Boxes, suggested: ['loading_docks', 'operating_hours'] },
  port: { label: msg('Port'), tone: 'caution', icon: Anchor, suggested: ['terminal', 'gate_hours'] },
  border_crossing: { label: msg('Border crossing'), tone: 'bad', icon: Flag, suggested: ['crossing_name', 'estimated_wait_time'] },
  other: { label: msg('Other'), tone: 'neutral', icon: MapPin, suggested: [] },
};

export const kindLabel = (k: string | null | undefined) => (k ? t(PLACE_KINDS[k]?.label ?? k) : '—');
export const kindOptions = (): SelectOption[] => Object.entries(PLACE_KINDS).map(([value, k]) => ({ value, label: t(k.label) }));

/** Pill tones for the map tones (the Badge has no 'primary'). */
export const pillTone = (g: GeoTone): Tone => (g === 'primary' ? 'info' : g);

// ---------------------------------------------------------------- shapes

export function toGeoShape(p: Pick<PlaceOut, 'id' | 'name' | 'kind' | 'color' | 'shape'>, selected = false): GeoShape | null {
  const sh = p.shape;
  const base = { id: p.id, label: p.name, tone: PLACE_KINDS[p.kind]?.tone ?? 'neutral', color: p.color, selected };
  if (sh.type === 'circle' && sh.center && sh.radius_m) return { ...base, type: 'circle', center: [sh.center.lon, sh.center.lat], radius_m: sh.radius_m };
  if (sh.type === 'polygon' && sh.points && sh.points.length >= 3) return { ...base, type: 'polygon', points: sh.points.map((q) => [q.lon, q.lat]) };
  return null;
}

/** The place's pin: the API's marker, else the circle's centre. */
export function placeAnchor(p: Pick<PlaceOut, 'marker' | 'shape'>): LngLat | null {
  if (p.marker) return [p.marker.lon, p.marker.lat];
  if (p.shape.center) return [p.shape.center.lon, p.shape.center.lat];
  return null;
}

export function shapeLabel(sh: PlaceOut['shape']): string {
  if (sh.type === 'circle') return t('Circle, {radius}', { radius: formatMetres(sh.radius_m) });
  return tp(sh.points?.length ?? 0, 'Polygon, {count} point', 'Polygon, {count} points');
}

export interface DraftShape {
  type: 'circle' | 'polygon';
  center: LngLat | null;
  radius_m: number;
  points: LngLat[];
}

export function draftFrom(sh: PlaceOut['shape'] | undefined, fallbackRadius = 200): DraftShape {
  if (!sh) return { type: 'circle', center: null, radius_m: fallbackRadius, points: [] };
  return {
    type: sh.type === 'polygon' ? 'polygon' : 'circle',
    center: sh.center ? [sh.center.lon, sh.center.lat] : null,
    radius_m: sh.radius_m ?? fallbackRadius,
    points: (sh.points ?? []).map((q) => [q.lon, q.lat]),
  };
}

/** The API shape for a drawn draft, or the reason it cannot be saved yet. */
export function shapeOf(d: DraftShape): { shape: Shape } | { error: string } {
  if (d.type === 'circle') {
    if (!d.center) return { error: t('Click the map to set the centre.') };
    if (!(d.radius_m > 0)) return { error: t('Enter a radius above 0 m.') };
    return { shape: { type: 'circle', center: { lon: d.center[0], lat: d.center[1] }, radius_m: Math.round(d.radius_m) } };
  }
  const distinct = new Set(d.points.map((q) => `${q[0]},${q[1]}`));
  if (distinct.size < 3) return { error: t('A polygon needs at least 3 points.') };
  return { shape: { type: 'polygon', points: d.points.map((q) => ({ lon: q[0], lat: q[1] })) } };
}

/** Did the drawn shape change compared with the saved one? */
export function sameShape(a: Shape, b: PlaceOut['shape']): boolean {
  const pts = (s: { points?: { lat: number; lon: number }[] | null }) => JSON.stringify((s.points ?? []).map((q) => [q.lat, q.lon]));
  if (a.type !== b.type) return false;
  if (a.type === 'circle')
    return a.center?.lat === b.center?.lat && a.center?.lon === b.center?.lon && Math.round(a.radius_m ?? 0) === Math.round(b.radius_m ?? 0);
  return pts(a) === pts(b);
}

// ---------------------------------------------------------------- assets

export const ASSET_STATUS: Record<string, { label: string; tone: Tone; geo: GeoTone }> = {
  moving: { label: msg('Moving'), tone: 'good', geo: 'good' },
  stationary: { label: msg('Stationary'), tone: 'medium', geo: 'medium' },
  offline: { label: msg('Offline'), tone: 'neutral', geo: 'neutral' },
  unknown: { label: msg('No position'), tone: 'neutral', geo: 'neutral' },
};

export const assetStatus = (k: string | null | undefined) => ASSET_STATUS[k ?? 'unknown'] ?? ASSET_STATUS.unknown!;

export const MOBILITY: Record<string, string> = { movable: msg('Movable'), stationary: msg('Stationary') };

/** Merge realtime frames into the asset map (assets with their own tracker). */
export function applyAssetFrames(data: AssetsLiveOut, frames: Map<string, LiveFrameUnit>): AssetsLiveOut {
  let changed = false;
  const units = data.units.map((u) => {
    const f = frames.get(`asset:${u.id}`);
    if (!f || (u.ts && new Date(u.ts).getTime() > f.ts * 1000)) return u;
    changed = true;
    const speed = f.speed ?? u.speed ?? null;
    return {
      ...u,
      ts: new Date(f.ts * 1000).toISOString(),
      lat: f.lat ?? u.lat,
      lon: f.lon ?? u.lon,
      speed,
      source: 'device',
      status: f.state === 'offline' ? 'offline' : (speed ?? 0) > 3 ? 'moving' : 'stationary',
    };
  });
  if (!changed) return data;
  const counts: Record<string, number> = {};
  for (const u of units) counts[u.status ?? 'unknown'] = (counts[u.status ?? 'unknown'] ?? 0) + 1;
  return { ...data, units, counts };
}

// ---------------------------------------------------------------- beacons

export const BEACON_KINDS: Record<string, string> = {
  asset_tag: msg('Asset tag'),
  driver_keyfob: msg('Driver keyfob'),
  sensor: msg('Sensor'),
};

// ---------------------------------------------------------------- asset rules

export const RULE_KINDS: Record<string, { label: string; help: string; icon: LucideIcon }> = {
  threshold: { label: msg('Threshold'), help: msg('A value crosses a limit (voltage, temperature, speed…).'), icon: Gauge },
  geofence: { label: msg('Geofence'), help: msg('The asset enters, leaves, is inside or outside a place.'), icon: MapPin },
  time_window: { label: msg('Time window'), help: msg('The asset moves outside (or inside) allowed hours.'), icon: Clock },
  connectivity: { label: msg('Connectivity'), help: msg('No position or message for too long.'), icon: WifiOff },
};

export const SEVERITIES: Record<string, { label: string; tone: Tone }> = {
  info: { label: msg('Info'), tone: 'info' },
  warning: { label: msg('Warning'), tone: 'medium' },
  critical: { label: msg('Critical'), tone: 'bad' },
};

export const OPERATORS: Record<string, string> = {
  gt: '>',
  ge: '≥',
  lt: '<',
  le: '≤',
  eq: '=',
  between: msg('between'),
  eq_true: msg('is on'),
  eq_false: msg('is off'),
};

export const TRIGGER_ON: Record<string, string> = {
  enter: msg('Enters the place'),
  exit: msg('Leaves the place'),
  inside: msg('Is inside the place'),
  outside: msg('Is outside the place'),
};

export const DAY_LABELS = [msg('Mon'), msg('Tue'), msg('Wed'), msg('Thu'), msg('Fri'), msg('Sat'), msg('Sun')];

type RuleLike = Pick<AssetRuleOut, 'kind' | 'config'>;

/**
 * One line describing what the rule checks, e.g. "Supply voltage < 11 V" — the field's label and
 * unit when the server described it (`fields`, from the rule catalogue), else its key.
 */
export function ruleSummary(r: RuleLike, placeName?: (id: string) => string | null | undefined, fields?: FieldLookup): string {
  const c = (r.config ?? {}) as Record<string, unknown>;
  switch (r.kind) {
    case 'threshold': {
      const op = String(c.op ?? 'gt');
      const key = String(c.field ?? '?');
      const field = fieldLabel(key, fields);
      const unit = fields?.(key)?.unit;
      const v = (x: unknown) => (x === undefined || x === null || x === '' ? '?' : unit ? `${String(x)} ${unit}` : String(x));
      if (op === 'eq_true' || op === 'eq_false') return `${field} ${t(OPERATORS[op] ?? op)}`;
      if (op === 'between') return t('{field} between {low} and {high}', { field, low: v(c.value), high: v(c.value2) });
      return `${field} ${OPERATORS[op] ?? op} ${v(c.value)}`;
    }
    case 'geofence': {
      const name = c.place_id ? (placeName?.(String(c.place_id)) ?? t('a place')) : t('no place chosen');
      return `${t(TRIGGER_ON[String(c.trigger_on ?? 'exit')] ?? String(c.trigger_on))}: ${name}`;
    }
    case 'time_window': {
      const days = Array.isArray(c.days) ? (c.days as number[]) : [0, 1, 2, 3, 4, 5, 6];
      const when = `${dayRange(days)} ${String(c.start ?? '00:00')}–${String(c.end ?? '23:59')}`;
      return c.trigger_on === 'inside' ? t('Moving within {when}', { when }) : t('Moving outside {when}', { when });
    }
    case 'connectivity':
      return t('Silent for more than {minutes} min', { minutes: String(c.max_offline_min ?? '—') });
    default:
      return '';
  }
}

/** "Mon–Fri", "Sat, Sun", "Every day". */
export function dayRange(days: number[]): string {
  const sorted = [...new Set(days)].filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b);
  if (sorted.length === 7) return t('Every day');
  if (!sorted.length) return t('No days');
  const contiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1]! + 1);
  if (contiguous && sorted.length > 2) return `${t(DAY_LABELS[sorted[0]!]!)}–${t(DAY_LABELS[sorted[sorted.length - 1]!]!)}`;
  return sorted.map((d) => t(DAY_LABELS[d]!)).join(', ');
}

export function targetSummary(target: Record<string, unknown> | null | undefined): string {
  const scope = String(target?.scope ?? 'all');
  const n = Array.isArray(target?.ids) ? target.ids.length : 0;
  if (scope === 'assets') return tp(n, '{count} asset', '{count} assets');
  if (scope === 'groups') return tp(n, '{count} group', '{count} groups');
  return t('All assets of the project');
}
